import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import PartCreateFilePicker from './PartCreateFilePicker';
import type { SelectedFile } from './PartCreateFilePicker';
import InvoiceTypeApproval from './InvoiceTypeApproval';
import {
  preprocessInvoice,
  createInvoice,
  type InvoicePreprocessResult,
} from '@/services/invoiceService';
import type { CreateInvoiceRequest } from '@/types/invoice';
import { fetchWithAuth } from '@/services/apiClient';
import { getFileContentUrl } from '@/services/docflowService';

type PipelineStep = 'file-picker' | 'preprocessing' | 'approval' | 'saving';

interface InvoiceProcessPipelineProps {
  onComplete: () => void;
  onCancel: () => void;
}

const InvoiceProcessPipeline: React.FC<InvoiceProcessPipelineProps> = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState<PipelineStep>('file-picker');
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [fileBase64, setFileBase64] = useState<string>('');
  const [fileMimeType, setFileMimeType] = useState<string>('');
  const [preprocessResult, setPreprocessResult] = useState<InvoicePreprocessResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const downloadDriveFile = async (fileId: string): Promise<{ base64: string; mimeType: string }> => {
    const url = getFileContentUrl(fileId);
    const response = await fetchWithAuth(url, {
      method: 'GET',
      headers: { 'Content-Type': '' },
    });
    const mimeType = response.headers.get('Content-Type') || 'application/pdf';
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        resolve({ base64: dataUrl.split(',')[1], mimeType });
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Step 1: File selected → preprocess (no AI)
  const handleFileSelected = async (file: SelectedFile) => {
    setSelectedFile(file);
    setError(null);
    setStep('preprocessing');

    try {
      let b64 = '';
      let mimeType = '';

      if (file.source === 'device' && file.localFile) {
        b64 = await fileToBase64(file.localFile);
        mimeType = file.localFile.type || 'application/pdf';
      } else if (file.source === 'drive' && file.driveFile) {
        const driveResult = await downloadDriveFile(file.driveFile.id);
        b64 = driveResult.base64;
        mimeType = driveResult.mimeType;
      } else {
        throw new Error('No file selected.');
      }

      setFileBase64(b64);
      setFileMimeType(mimeType);

      const result = await preprocessInvoice({
        file_base64: b64,
        mime_type: mimeType,
      });

      setPreprocessResult(result);
      setStep('approval');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preprocessing failed');
    }
  };

  // Step 2: User confirms → save directly to Firestore + Sheets
  const handleConfirm = async (
    invoiceType: 'purchase' | 'sales',
    editedMetadata: Record<string, { value: string; bbox: { x: number; y: number; w: number; h: number } | null }>,
    items: Array<{ name: { value: string }; spec: { value: string }; qty: { value: string }; price: { value: string } }>,
  ) => {
    setError(null);
    setStep('saving');

    try {
      const invoiceItems = items
        .filter(item => item.name.value.trim())
        .map((item, i) => ({
          line_no: i + 1,
          product_name: item.name.value.trim(),
          specification: item.spec.value.trim(),
          quantity: parseFloat(item.qty.value) || 1,
          unit_price: parseFloat(item.price.value) || 0,
          amount: (parseFloat(item.qty.value) || 1) * (parseFloat(item.price.value) || 0),
          currency: 'TWD',
        }));

      const req: CreateInvoiceRequest = {
        invoice_number: editedMetadata.invoice_number?.value || undefined,
        invoice_type: invoiceType,
        invoice_date: editedMetadata.invoice_date?.value || new Date().toISOString().slice(0, 10),
        due_date: editedMetadata.invoice_date?.value || new Date().toISOString().slice(0, 10),
        counterparty_name: invoiceType === 'purchase'
          ? editedMetadata.seller_name?.value || ''
          : editedMetadata.buyer_name?.value || '',
        seller_name: editedMetadata.seller_name?.value || undefined,
        seller_id: editedMetadata.seller_unified_number?.value || undefined,
        buyer_name: editedMetadata.buyer_name?.value || undefined,
        buyer_id: editedMetadata.buyer_unified_number?.value || undefined,
        source_po_id: editedMetadata.order_id?.value || undefined,
        items: invoiceItems.length > 0 ? invoiceItems : [{
          line_no: 1,
          product_name: 'Invoice item',
          specification: '',
          quantity: 1,
          unit_price: parseFloat(editedMetadata.sale_amount?.value || '0') || 0,
          amount: parseFloat(editedMetadata.sale_amount?.value || '0') || 0,
          currency: 'TWD',
        }],
        currency: 'TWD',
        tax_rate: (() => {
          const sale = parseFloat(editedMetadata.sale_amount?.value || '0') || 0;
          const tax = parseFloat(editedMetadata.tax_amount?.value || '0') || 0;
          return sale > 0 ? tax / sale : 0.05;
        })(),
        notes: '',
      };

      await createInvoice(req);
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save invoice');
    }
  };

  switch (step) {
    case 'file-picker':
      return <PartCreateFilePicker onSelect={handleFileSelected} onCancel={onCancel} />;

    case 'preprocessing':
      return (
        <LoadingOverlay
          message={error || 'Processing document...'}
          error={!!error}
          onCancel={onCancel}
          onBack={() => { setError(null); setStep('file-picker'); }}
          onRetry={error && selectedFile ? () => handleFileSelected(selectedFile) : undefined}
        />
      );

    case 'approval':
      return preprocessResult ? (
        <InvoiceTypeApproval
          detection={{
            is_invoice: true,
            invoice_type: 'unknown' as const,
            confidence: Object.keys(preprocessResult.metadata).length > 2 ? 0.7 : 0.3,
            detected_language: '',
            reasoning: `Extracted ${Object.keys(preprocessResult.metadata).length} fields via text analysis`,
            hints: {},
            metadata: {
              ...preprocessResult.metadata,
              page_dimensions: preprocessResult.page_dimensions,
            } as Record<string, { value: unknown; bbox?: { x: number; y: number; w: number; h: number } }>,
            format: preprocessResult.format_hint,
          }}
          fileBase64={preprocessResult.page_image}
          fileMimeType={preprocessResult.page_image_mime || 'image/png'}
          onConfirm={handleConfirm}
          onBack={() => setStep('file-picker')}
          onCancel={onCancel}
        />
      ) : null;

    case 'saving':
      return (
        <LoadingOverlay
          message={error || 'Saving invoice...'}
          error={!!error}
          onCancel={onCancel}
          onBack={() => { setError(null); setStep('approval'); }}
          onRetry={undefined}
        />
      );

    default:
      return null;
  }
};

const LoadingOverlay: React.FC<{
  message: string;
  error?: boolean;
  onCancel: () => void;
  onBack?: () => void;
  onRetry?: (() => void) | undefined;
}> = ({ message, error, onCancel, onBack, onRetry }) => (
  <div className="fixed inset-0 z-[70] flex items-center justify-center">
    <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
    <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl px-8 py-10 flex flex-col items-center gap-4 max-w-sm mx-4 w-full">
      {!error && <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />}
      <p className={`text-sm text-center ${error ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-300'}`}>
        {message}
      </p>
      <div className="flex gap-2 mt-2">
        {onBack && (
          <button onClick={onBack} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            Back
          </button>
        )}
        {onRetry && (
          <button onClick={onRetry} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Retry
          </button>
        )}
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
          Cancel
        </button>
      </div>
    </div>
  </div>
);

export default InvoiceProcessPipeline;
